import { Head } from "vite-react-ssg";
import {
  absoluteUrl,
  DEFAULT_OG_IMAGE,
  SITE_NAME,
  TWITTER_HANDLE,
} from "@/lib/seo";
import { useLanguage } from "@/contexts/LanguageContext";
import type { Locale } from "@/content/types";
import { DIR, OG_LOCALE, HTML_LANG, localizedPath } from "@/lib/locale";

interface SeoProps {
  title: string;
  description: string;
  path: string;
  keywords?: string;
  image?: string;
  imageAlt?: string;
  /** Locale/language for this page. When provided, takes precedence over the
   *  UI language from useLanguage. Recommended for pages that are genuinely
   *  in a specific language (e.g. locale-derived routes). */
  locale?: Locale;
  /** og:locale value. Auto-derived from locale if not provided. */
  ogLocale?: "en_US" | "ar_EG";
  /** lang attribute. Auto-derived from locale if not provided. */
  lang?: string;
  /** dir attribute. Auto-derived from locale if not provided. */
  dir?: "ltr" | "rtl";
  noindex?: boolean;
  /**
   * Locales for which this page actually exists. Defaults to both.
   * Pass a subset when a page has no counterpart in the other language —
   * declaring an hreflang alternate that 404s is an error.
   */
  alternates?: Locale[];
  schema?: object | object[];
  /** Social-specific overrides, for when the share headline should differ from
   *  the <title> (which is written for a SERP, not for a Facebook card). */
  ogTitle?: string;
  ogDescription?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  /** Emit the clinic's geo meta tags. Only meaningful on pages that represent
   *  the physical practice itself (home, contact) — not on every service page. */
  geo?: boolean;
}

// Default OG image is a 1200x630 asset (the standard OG/Twitter "large image"
// size) — see DEFAULT_OG_IMAGE in src/lib/seo.ts. If a caller passes a custom
// `image` of different dimensions, these values may be inaccurate; there's no
// cheap way to read real image dimensions at build time here, so we keep this
// as a reasonable default rather than over-engineering an image-probing step.
const DEFAULT_OG_IMAGE_WIDTH = 1200;
const DEFAULT_OG_IMAGE_HEIGHT = 630;

const CLINIC_GEO = {
  region: "EG-C",
  placename: "Cairo",
  latitude: "30.0733141",
  longitude: "31.3387106",
};

const Seo = ({
  title,
  description,
  path,
  keywords,
  image = DEFAULT_OG_IMAGE,
  imageAlt,
  locale,
  ogLocale,
  lang,
  dir,
  noindex = false,
  alternates = ["en", "ar"],
  schema,
  ogTitle,
  ogDescription,
  twitterTitle,
  twitterDescription,
  geo = false,
}: SeoProps) => {
  // Locale can be passed explicitly (preferred for locale-derived routes) or
  // read from the current UI language context. For locale-derived routes
  // (/en/... and /ar/...), always pass the locale prop explicitly so metadata
  // is accurate during SSG.
  const contextLanguage = useLanguage().language;
  const resolvedLocale = locale ?? contextLanguage;
  const resolvedLang = lang ?? HTML_LANG[resolvedLocale];
  const resolvedDir = dir ?? DIR[resolvedLocale];
  const resolvedOgLocale = ogLocale ?? OG_LOCALE[resolvedLocale];

  const url = absoluteUrl(path);
  const schemas = schema ? (Array.isArray(schema) ? schema : [schema]) : [];
  const resolvedImageAlt = imageAlt ?? title;
  /** The page's path with its locale prefix removed, e.g. "/services/veneers". */
  const barePath = path.replace(/^\/(en|ar)(?=\/|$)/, "") || "/";

  return (
    <Head>
      <html lang={resolvedLang} dir={resolvedDir} />
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      {/*
        Canonical and alternate are emitted only for indexable pages. Pairing
        rel=canonical with noindex sends search engines two contradictory
        instructions about the same URL ("don't index this" / "this is the
        preferred URL to index"), and Google's guidance is not to combine them.
        The 404 route is the only noindex page here.
      */}
      {!noindex && <link rel="canonical" href={url} />}
      {/*
        hreflang alternates.

        Three things here are deliberate and were each a bug in an earlier revision:

        1. Each <link> is a DIRECT child of <Head>. Wrapping them in a React
           fragment causes react-helmet to silently drop them — they never reach
           the HTML, and nothing warns you. Do not group these in a <>...</>.
        2. Hrefs are built by swapping the locale SEGMENT via localizedPath(),
           not by string-replacing "/en/" for "/ar/". That replacement cannot
           match the homepage ("…/en" has no trailing slash), so the homepage
           silently advertised itself as its own Arabic alternate.
        3. Only locales that actually HAVE the page are emitted, via the
           `alternates` prop. Declaring an alternate that 404s is an hreflang
           error, and /ar/services/pediatric-dentistry genuinely has no English
           counterpart yet. Omitting an absent alternate is correct; inventing a
           machine-translated page to satisfy symmetry is not.

        x-default points at English, and is emitted only when English exists.
      */}
      {!noindex &&
        alternates.map((altLocale) => (
          <link
            key={altLocale}
            rel="alternate"
            hrefLang={altLocale}
            href={absoluteUrl(localizedPath(altLocale, barePath))}
          />
        ))}
      {!noindex && alternates.includes("en") && (
        <link
          rel="alternate"
          hrefLang="x-default"
          href={absoluteUrl(localizedPath("en", barePath))}
        />
      )}
      <meta
        name="robots"
        content={noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"}
      />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={ogTitle ?? title} />
      <meta property="og:description" content={ogDescription ?? description} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content={String(DEFAULT_OG_IMAGE_WIDTH)} />
      <meta property="og:image:height" content={String(DEFAULT_OG_IMAGE_HEIGHT)} />
      <meta property="og:image:alt" content={resolvedImageAlt} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content={resolvedOgLocale} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={TWITTER_HANDLE} />
      <meta name="twitter:title" content={twitterTitle ?? ogTitle ?? title} />
      <meta
        name="twitter:description"
        content={twitterDescription ?? ogDescription ?? description}
      />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content={resolvedImageAlt} />

      {geo && <meta name="geo.region" content={CLINIC_GEO.region} />}
      {geo && <meta name="geo.placename" content={CLINIC_GEO.placename} />}
      {geo && (
        <meta
          name="geo.position"
          content={`${CLINIC_GEO.latitude};${CLINIC_GEO.longitude}`}
        />
      )}
      {geo && (
        <meta
          name="ICBM"
          content={`${CLINIC_GEO.latitude}, ${CLINIC_GEO.longitude}`}
        />
      )}

      {schemas.map((s, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(s)}
        </script>
      ))}
    </Head>
  );
};

export default Seo;
